/**
 * Studio (§31) — header slots for the knowledge base's two tabs.
 *
 * The mock puts "Songs by theme"'s own controls (its counts sub, its search and
 * its Add theme / Bulk import pair) into the SHARED header, but that state
 * lives inside SongsByThemeManager. Rather than hoist it (which would change
 * behaviour), KnowledgeBaseManager renders empty slots in the header and
 * SongsByThemeManager portals its existing controls into them. Nothing moves in
 * the React tree, no state changes owner, and classic never mounts either side.
 *
 * Mock frames 19, 20.
 *
 * The ref callbacks MUST be stable. The first version built one on every call,
 * so React detached and reattached the node each render, `setSlots` fired every
 * time and the songs tab crashed the error boundary ("Something unexpected
 * happened", caught in capture f19). They are cached per name below.
 */
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const SlotCtx = createContext(null);

/** The provider: gives back `{ slots, claim, Provider }` for the header. */
export function useChromeSlots() {
  const [slots, setSlots] = useState({});
  const refs = useRef({});
  const claim = useCallback((name) => {
    if (!refs.current[name]) {
      refs.current[name] = (node) => {
        setSlots((prev) => {
          const next = node || undefined;
          return prev[name] === next ? prev : { ...prev, [name]: next };
        });
      };
    }
    return refs.current[name];
  }, []);
  return { slots, claim, Provider: SlotCtx.Provider };
}

/** Child side: render `children` into the named header slot, when it exists. */
export function Slotted({ name, children }) {
  const slots = useContext(SlotCtx);
  const node = slots && slots[name];
  return node ? createPortal(children, node) : null;
}
